import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { from } from 'rxjs';
import { exhaustMap, filter, map, startWith, switchMap, tap } from 'rxjs/operators';

import { FeatureFlagKeys } from '@ninety/platform/models/feature-flag/feature-flag-state.model';
import { DeployedVersionService } from '@ninety/platform/services/services/deployed-version/deployed-version.service';
import { ReloadPromptPolicy } from '@ninety/platform/services/services/user-activity/reload-prompt-policy';
import { UserActivityService } from '@ninety/platform/services/services/user-activity/user-activity.service';

import { selectFeatureFlag } from '../../app-entities/feature-flag/feature-flag-state.selectors';
import { appActions } from '../../app.actions';
import { CONFIRM_DIALOG } from '../../tokens/billing-dialog.tokens';
import { RELOAD_TOAST } from '../../tokens/reload-toast.tokens';

import { UserActivityActions } from './user-activity.actions';

@Injectable()
export class UserActivityEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly userActivityService = inject(UserActivityService);
  private readonly deployedVersionService = inject(DeployedVersionService);

  private readonly openReloadToast = inject(RELOAD_TOAST);
  private readonly policy = inject(ReloadPromptPolicy);

  private readonly openConfirm = inject(CONFIRM_DIALOG);

  initUserActivityMonitoring$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.bootstrap),
      switchMap(() => this.userActivityService.returnedFromIdle$),
      map(idleSeconds => UserActivityActions.activityRestored({ idleSeconds }))
    )
  );

  initUpdateCheckTimer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.bootstrap),
      switchMap(() => this.userActivityService.updateCheckTimer$),
      concatLatestFrom(() => this.userActivityService.activity$),
      filter(([_, activity]) => activity.status === 'active'),
      map(() => UserActivityActions.checkForAppUpdates())
    )
  );

  captureBaselineVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.bootstrap),
        switchMap(() => this.deployedVersionService.getLiveVersion()),
        tap(version => this.policy.setBaseline(version))
      ),
    { dispatch: false }
  );

  /** Shows the inactivity reload prompt once the idle threshold is crossed, if inactivityDialog is enabled.
   * Ignores this if simpleVersionNotification flag is enabled, in favor of showAppUpdatedToast$. */
  showInactivityReloadPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActivityActions.activityRestored),
      filter(({ idleSeconds }) => idleSeconds >= this.userActivityService.reloadThresholdSeconds),
      concatLatestFrom(() => [
        this.store.select(selectFeatureFlag(FeatureFlagKeys.inactivityDialog)),
        this.store.select(selectFeatureFlag(FeatureFlagKeys.simpleVersionNotification)),
      ]),
      filter(
        ([, dialogEnabled, useToastUpdateNotification]) =>
          dialogEnabled !== false && useToastUpdateNotification !== true
      ),
      map(([{ idleSeconds }]) => idleSeconds),
      // 1-decimal hours (e.g. 4.7) — a precise numeric averages meaningfully in Gainsight PX,
      // unlike a floored whole-hour value which collapses everything just above the threshold.
      map(idleSeconds => Math.round((idleSeconds / 3600) * 10) / 10),
      exhaustMap(hoursInactive =>
        this.openConfirm({
          // Sizes the dialog to 400px via confirm-dialog.scss.
          panelClass: 'session-update-dialog',
          disableClose: true,
          data: {
            title: `We've updated our software`,
            message: `While you were away, we launched an update. Refresh your browser for the best experience.`,
            additionalMessage: `<strong>Note:</strong> If you have unsaved edits, copy them somewhere safe before reloading.`,
            confirmButtonText: 'Reload',
            cancelButtonText: 'Not now',
          },
        })
          .afterClosed()
          .pipe(
            map(shouldReload =>
              UserActivityActions.inactivityReloadPromptClosed({ hoursInactive, shouldReload: !!shouldReload })
            ),
            startWith(UserActivityActions.inactivityReloadPromptOpened({ hoursInactive }))
          )
      )
    )
  );

  /** Shows the reload toast when ReloadPromptPolicy says so on a timer.
   *  Emits an `opened` action immediately (via startWith, as the toast opens) and a `closed` action carrying the user's
   *  choice when it closes. Any close other than Refresh counts as "Not now".
   *  Behavior (reload) and analytics live in their own effects. */
  showAppUpdateToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActivityActions.checkForAppUpdates),
      concatLatestFrom(() => [this.store.select(selectFeatureFlag(FeatureFlagKeys.simpleVersionNotification))]),
      filter(([, updateNotifications]) => updateNotifications === true),
      switchMap(() =>
        from(this.deployedVersionService.getLiveVersion()).pipe(
          filter(liveVersion => this.policy.shouldPrompt(liveVersion))
        )
      ),
      exhaustMap(liveVersion =>
        this.openReloadToast().pipe(
          tap(shouldReload => {
            if (!shouldReload) this.policy.recordDismissed(liveVersion);
          }),
          map(shouldReload => UserActivityActions.newVersionPromptClosed({ version: liveVersion, shouldReload })),
          startWith(UserActivityActions.newVersionPromptOpened({ version: liveVersion }))
        )
      )
    )
  );

  reloadOnPromptConfirmed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActivityActions.inactivityReloadPromptClosed, UserActivityActions.newVersionPromptClosed),
        filter(({ shouldReload }) => shouldReload),
        tap(() => this.reloadPage())
      ),
    { dispatch: false }
  );

  public reloadPage(): void {
    window.location.reload();
  }
}
